import { gql } from '@apollo/client';
import { useEffect, useState } from 'react';
import { useHistory } from 'react-router';

import { useNotificationContext } from 'core/context/Notifications';
import { POLL_INTERVAL } from 'core/organisms/skiptochallenge/operations';
import embedEvent from 'play/support/embedEvent';
import { useTrackAmplitudeEvent } from 'support/amplitude/amplitudeEvent';
import { createPathPrefix } from 'support/location';
import {
  Challenge,
  ChallengeStatus,
  Mode,
  Track,
  useChallengeStatusLazyQuery,
  useSkipChallengeMutation,
  useSkipToChallengeMutation,
} from 'types/graphql';

import { useSkilljar } from './skilljar';

export interface SkipChallengeProps {
  teamSlug: string;
  track: Pick<Track, 'id' | 'slug'>;
  challenge?: Pick<Challenge, 'id' | 'failed' | 'status' | 'slug' | 'type' | 'index'>;
  nextChallenge?: Pick<Challenge, 'id' | 'permalink'>;
  mode: Mode;
  inviteID?: string | null;
  userID?: string | null;
  onFail?: () => void;
  trackReviewsEnabled?: boolean;
  totalChallenges: number;
}

export const useSkipToChallenge = ({
  teamSlug,
  track,
  challenge,
  nextChallenge,
  mode,
  inviteID = null,
  userID = null,
  onFail,
  trackReviewsEnabled,
  totalChallenges,
}: SkipChallengeProps) => {
  const history = useHistory();
  const { dispatch } = useNotificationContext();
  const [skipChallenge] = useSkipChallengeMutation();
  const [skipToChallenge] = useSkipToChallengeMutation();
  const [loading, setLoading] = useState(false);
  const [query, { data, stopPolling }] = useChallengeStatusLazyQuery({
    pollInterval: POLL_INTERVAL,
    fetchPolicy: 'network-only',
  });
  const trackAmplitudeEvent = useTrackAmplitudeEvent();
  const { sendSkilljarEmbedEvent } = useSkilljar();

  useEffect(() => {
    if ((data?.challenge?.failed || 0) > 0) {
      onFail?.();
      if (stopPolling) {
        stopPolling();
      }
      setLoading(false);
      dispatch(
        'error',
        'Failed to skip to challenge',
        data?.challenge?.failMessage ?? 'Unable to skip to challenge, please try again.',
        8,
      );
    }
  }, [challenge?.id, data?.challenge?.failMessage, data?.challenge?.failed, dispatch, onFail, stopPolling]);

  useEffect(() => {
    if (nextChallenge) {
      if (data?.challenge?.status === ChallengeStatus.Unlocked) {
        const impersonateParams = inviteID && userID ? `?inviteID=${inviteID}&userID=${userID}` : '';
        history.push(
          `${createPathPrefix({ mode, inviteID })}${nextChallenge.permalink}/notes${impersonateParams}`,
          history.location.state,
        );
      }
    } else {
      if (data?.challenge?.status === ChallengeStatus.Completed) {
        if (trackReviewsEnabled) {
          history.push(
            `${createPathPrefix({ mode, inviteID })}/${teamSlug}/tracks/${track.slug}/recap`,
            history.location.state,
          );
        } else {
          history.push(
            `${createPathPrefix({ mode, inviteID })}/${teamSlug}/tracks/${track.slug}`,
            history.location.state,
          );
        }
      }
    }
  }, [
    data?.challenge?.status,
    history,
    nextChallenge,
    mode,
    inviteID,
    teamSlug,
    track.slug,
    trackReviewsEnabled,
    userID,
  ]);

  const onSkipChallenge = async (nextChallengeId?: string) => {
    if (!challenge) {
      return;
    }

    setLoading(true);

    embedEvent('track.challenge_skipped', { track_slug: track.slug, challenge_slug: challenge.slug });
    trackAmplitudeEvent({ name: 'Lab - Challenge - Skipped', data: { type: challenge.type } });
    sendSkilljarEmbedEvent({
      challengesPassed: challenge.index + 1,
      challengesTotal: totalChallenges,
      trackFinished: challenge.index + 1 === totalChallenges,
    });

    try {
      if (nextChallengeId) {
        await skipToChallenge({
          variables: { inviteID, userID, trackID: track.id, challengeID: nextChallengeId, force: false },
        });
        query({ variables: { inviteID, userID, challengeID: nextChallengeId! } });
      } else {
        await skipChallenge({
          variables: { inviteID, userID, trackID: track.id, challengeID: challenge.id },
        });
        query({ variables: { inviteID, userID, challengeID: challenge.id } });
      }
    } catch (error) {
      onFail?.();
      dispatch('error', 'Failed to skip to challenge', 'Unable to skip to challenge, please try again.', 8);
    }
  };

  return { loading, skipToChallenge: onSkipChallenge };
};

gql`
  query ChallengeStatus($challengeID: String!, $userID: String, $inviteID: String) {
    challenge(challengeID: $challengeID, userID: $userID, inviteID: $inviteID) {
      status
      failed
      failMessage
    }
  }
`;
